class LocalStorageCacher {
    /**
     * Field prefix.
     *
     * @type {string}
     * @private
     */
    prefix;
    /**
     * Cacher constructor.
     *
     * @param {string} prefix
     */
    constructor(prefix) {
        this.prefix = prefix ?? "cacher-";
    }
    /**
     * Delete data by key.
     *
     * @param {string} key
     */
    del(key) {
        if (typeof window !== "undefined") {
            localStorage.removeItem(`${this.prefix}${key}`);
        }
    }
    /**
     * Get data by key.
     *
     * @param {string} key
     * @return {T | null}
     */
    get(key) {
        if (typeof window === "undefined") {
            return null;
        }
        const raw = localStorage.getItem(`${this.prefix}${key}`);
        if (raw === null) {
            return null;
        }
        const content = JSON.parse(raw);
        if (content.expiry !== null && content.expiry <= Date.now()) {
            this.del(key);
            return null;
        }
        return content.payload;
    }
    /**
     * Check the key for existence.
     *
     * @param {string} key
     * @return {boolean}
     */
    has(key) {
        if (typeof window === "undefined") {
            return false;
        }
        const raw = localStorage.getItem(`${this.prefix}${key}`);
        if (raw === null) {
            return false;
        }
        const content = JSON.parse(raw);
        if (content.expiry !== null && content.expiry <= Date.now()) {
            this.del(key);
            return false;
        }
        return true;
    }
    /**
     * Clear storage.
     */
    purge() {
        if (typeof window === "undefined") {
            return;
        }
        const length = localStorage.length;
        const items = [];
        for (let index = 0; index < length; index++) {
            if (localStorage.key(index)?.includes(this.prefix) === true) {
                items.push(localStorage.key(index));
            }
        }
        items.forEach((item) => {
            if (item !== null) {
                localStorage.removeItem(item);
            }
        });
    }
    /**
     * Put data into storage.
     *
     * @param {string} key
     * @param {T} data
     * @param {Options} options
     */
    put(key, data, options) {
        if (typeof window === "undefined") {
            return;
        }
        const expiry = typeof options?.ttl === "number"
            ? Date.now() + options.ttl
            : null;
        const content = {
            expiry,
            payload: data
        };
        const raw = JSON.stringify(content);
        localStorage.setItem(`${this.prefix}${key}`, raw);
    }
    deleteExpired() {
        if (typeof window === "undefined") {
            return;
        }
        const length = localStorage.length;
        for (let index = 0; index < length; index++) {
            const key = localStorage.key(index);
            if (key?.includes(this.prefix) === true) {
                const raw = localStorage.getItem(key);
                if (raw === null) {
                    continue;
                }
                const content = JSON.parse(raw);
                if (content.expiry !== null && content.expiry <= Date.now()) {
                    this.del(key.replace(this.prefix, ""));
                }
            }
        }
    }
}

class CacherError extends Error {
    constructor(message) {
        super(message);
        this.name = "CacherError";
    }
}

class IndexedDbCacher {
    dbName;
    storeName;
    indexName = "expiry";
    quotaLimit;
    transactionTimeout;
    purgingTimeout;
    batchSize;
    exponentialBackoffBaseMs = 50;
    dbVersion = 1;
    isPurging = false;
    purgingTimer = null;
    constructor(options) {
        this.dbName = options?.dbName ?? "app-database";
        this.storeName = options?.storeName ?? "cache";
        this.quotaLimit = options?.quotaLimitMb ?? 3;
        this.transactionTimeout = options?.transactionTimeoutMs ?? 5000;
        this.purgingTimeout = options?.purgingTimeoutMs ?? 10000;
        this.batchSize = options?.batchSize ?? 50;
    }
    async withRetry(fn, attempts = 3) {
        try {
            return await fn();
        }
        catch (e) {
            const message = e?.message || String(e);
            const name = e?.name || '';
            const isTransient = name === 'AbortError' ||
                name === 'TimeoutError' ||
                name === 'UnknownError' ||
                name === 'VersionError' ||
                name === 'InvalidStateError' ||
                /Transaction timed out|was aborted|The transaction was aborted|connection closed/i.test(message);
            if (isTransient && attempts > 1) {
                const delay = this.exponentialBackoffBaseMs * Math.pow(2, 3 - attempts);
                await new Promise(r => setTimeout(r, delay));
                return this.withRetry(fn, attempts - 1);
            }
            throw e;
        }
    }
    setPurging(value) {
        this.isPurging = value;
        if (this.purgingTimer) {
            clearTimeout(this.purgingTimer);
            this.purgingTimer = null;
        }
        if (value) {
            // Safety timeout - auto-reset isPurging after 10s to prevent deadlock
            this.purgingTimer = setTimeout(() => {
                console.warn('IndexedDB: Purging flag forcefully reset after timeout');
                this.isPurging = false;
                this.purgingTimer = null;
            }, this.purgingTimeout);
        }
    }
    async waitForNotPurging(maxWaitMs = 2000) {
        if (!this.isPurging)
            return;
        const start = Date.now();
        await new Promise((resolve, reject) => {
            const check = () => {
                if (!this.isPurging)
                    return resolve();
                if (Date.now() - start >= maxWaitMs)
                    return reject(new CacherError('Timed out waiting for purge to finish'));
                setTimeout(check, 50);
            };
            check();
        });
    }
    isQuotaExceededError(e) {
        const name = e?.name || e?.target?.error?.name || '';
        const message = e?.message || e?.target?.error?.message || '';
        return /QuotaExceededError/i.test(name) || /quota exceeded/i.test(message);
    }
    async del(key) {
        await this.waitForNotPurging();
        return this.withRetry(() => this.executeTransaction('readwrite', (store) => store.delete(key)));
    }
    async get(key) {
        await this.waitForNotPurging();
        return this.withRetry(async () => {
            const result = await this.executeTransaction('readonly', (store) => store.get(key));
            if (result) {
                const { payload, expiry } = result;
                // Check if expired
                if (expiry !== null && expiry < Date.now()) {
                    // Lazy delete expired item (fire and forget)
                    this.del(key).catch(() => { });
                    return null;
                }
                return JSON.parse(payload);
            }
            return null;
        });
    }
    async has(key) {
        await this.waitForNotPurging();
        return this.withRetry(async () => {
            const result = await this.executeTransaction('readonly', (store) => store.get(key));
            if (result === undefined) {
                return false;
            }
            const { expiry } = result;
            return expiry === null || expiry >= Date.now();
        });
    }
    async purge() {
        await this.waitForNotPurging();
        this.setPurging(true);
        try {
            return await this.withRetry(() => this.executeTransaction('readwrite', (store) => store.clear()));
        }
        finally {
            this.setPurging(false);
        }
    }
    async put(key, data, options) {
        await this.waitForNotPurging();
        const attemptPut = () => {
            const expiry = options?.ttl ? options.ttl + Date.now() : null;
            return this.executeTransaction('readwrite', (store) => store.put({ payload: JSON.stringify(data), expiry }, key));
        };
        try {
            await this.withRetry(attemptPut);
        }
        catch (e) {
            if (!this.isQuotaExceededError(e)) {
                throw e instanceof Error ? e : new CacherError(String(e));
            }
            // 1) Try to clean up expired items
            try {
                await this.withRetry(() => this.deleteExpired());
            }
            catch (_) {
                // ignore cleanup failure, we'll try other ways
            }
            try {
                await this.withRetry(attemptPut);
                return;
            }
            catch (e2) {
                if (!this.isQuotaExceededError(e2)) {
                    throw e2 instanceof Error ? e2 : new CacherError(String(e2));
                }
                // 2) Hard measure: delete the database and try again
                try {
                    this.setPurging(true);
                    await this.deleteDBase();
                }
                finally {
                    this.setPurging(false);
                }
                await this.withRetry(attemptPut);
            }
        }
    }
    async deleteExpired() {
        await this.waitForNotPurging();
        this.setPurging(true);
        try {
            return await this.withRetry(() => this.deleteExpiredBatch());
        }
        finally {
            this.setPurging(false);
        }
    }
    async deleteExpiredBatch() {
        let hasMore = true;
        while (hasMore) {
            hasMore = await new Promise((resolve, reject) => {
                const handle = window.indexedDB.open(this.dbName, this.dbVersion);
                let db = null;
                const cleanup = () => {
                    clearTimeout(timeoutId);
                    if (db) {
                        try {
                            db.close();
                        }
                        catch (e) {
                            // Ignore
                        }
                    }
                };
                const timeoutId = setTimeout(() => {
                    cleanup();
                    reject(new CacherError('Failed to delete expired cache entries: operation timed out'));
                }, this.transactionTimeout);
                handle.onerror = (e) => {
                    cleanup();
                    const error = e.target?.error;
                    reject(new CacherError(`Failed to open cache database: ${error?.message || JSON.stringify(error)}`));
                };
                handle.onupgradeneeded = (e) => {
                    const request = e.target;
                    const database = request.result;
                    if (!database) {
                        request.transaction?.abort();
                        cleanup();
                        reject(new CacherError("Failed to upgrade cache database"));
                        return;
                    }
                    let store;
                    if (!database.objectStoreNames.contains(this.storeName)) {
                        store = database.createObjectStore(this.storeName);
                    }
                    else {
                        store = request.transaction.objectStore(this.storeName);
                    }
                    // Always ensure index exists
                    if (!store.indexNames.contains(this.indexName)) {
                        store.createIndex(this.indexName, this.indexName);
                    }
                };
                handle.onsuccess = (e) => {
                    try {
                        db = e.target.result;
                        const tx = db.transaction(this.storeName, "readwrite");
                        tx.oncomplete = () => {
                            cleanup();
                        };
                        tx.onabort = () => {
                            cleanup();
                            reject(new CacherError('Transaction was aborted'));
                        };
                        tx.onerror = (e) => {
                            cleanup();
                            const error = e.target?.error;
                            reject(new CacherError(`Transaction error: ${error?.message || JSON.stringify(error)}`));
                        };
                        const store = tx.objectStore(this.storeName);
                        // Check if the index exists
                        if (!store.indexNames.contains(this.indexName)) {
                            cleanup();
                            reject(new CacherError(`Index "${this.indexName}" does not exist on store "${this.storeName}"`));
                            return;
                        }
                        const index = store.index(this.indexName);
                        const req = index.openKeyCursor(IDBKeyRange.upperBound(Date.now()));
                        let count = 0;
                        let hasMoreItems = false;
                        req.onerror = (e) => {
                            cleanup();
                            const error = e.target?.error;
                            reject(new CacherError(`Failed to query expired cache entries: ${error?.message || JSON.stringify(error)}`));
                        };
                        req.onsuccess = () => {
                            const cursor = req.result;
                            if (cursor && count < this.batchSize) {
                                count++;
                                const deleteReq = store.delete(cursor.primaryKey);
                                deleteReq.onerror = (e) => {
                                    cleanup();
                                    const error = e.target?.error;
                                    reject(new CacherError(`Failed to delete key ${cursor.primaryKey}: ${error?.message || JSON.stringify(error)}`));
                                };
                                deleteReq.onsuccess = () => {
                                    cursor.continue();
                                };
                            }
                            else if (cursor && count >= this.batchSize) {
                                // Hit batch limit, there are more items
                                hasMoreItems = true;
                                resolve(hasMoreItems);
                            }
                            else {
                                // No more items
                                resolve(false);
                            }
                        };
                    }
                    catch (err) {
                        cleanup();
                        reject(err instanceof Error ? err : new CacherError(String(err)));
                    }
                };
            });
        }
    }
    async executeTransaction(mode, operation) {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject(new CacherError('Transaction timed out'));
            }, this.transactionTimeout);
            const handle = window.indexedDB.open(this.dbName, this.dbVersion);
            let db = null;
            const cleanup = () => {
                clearTimeout(timeoutId);
                if (db) {
                    try {
                        db.close();
                    }
                    catch (e) {
                        // Ignore close errors
                    }
                }
            };
            handle.onerror = (e) => {
                cleanup();
                const error = e.target?.error;
                reject(new CacherError(`Failed to open cache database: ${error?.message || JSON.stringify(error) || JSON.stringify(e)}`));
            };
            handle.onupgradeneeded = (e) => {
                const request = e.target;
                const database = request.result;
                if (!database) {
                    request.transaction?.abort();
                    cleanup();
                    reject(new CacherError("Failed to upgrade cache database"));
                    return;
                }
                try {
                    let store;
                    if (!database.objectStoreNames.contains(this.storeName)) {
                        store = database.createObjectStore(this.storeName);
                    }
                    else {
                        store = request.transaction.objectStore(this.storeName);
                    }
                    // Always ensure index exists
                    if (!store.indexNames.contains(this.indexName)) {
                        store.createIndex(this.indexName, this.indexName);
                    }
                }
                catch (err) {
                    request.transaction?.abort();
                    cleanup();
                    reject(err);
                }
            };
            handle.onsuccess = (event) => {
                try {
                    db = event.target.result;
                    const tx = db.transaction(this.storeName, mode);
                    tx.oncomplete = () => {
                        cleanup();
                    };
                    tx.onabort = () => {
                        cleanup();
                        reject(new CacherError('Transaction was aborted'));
                    };
                    tx.onerror = (e) => {
                        cleanup();
                        const error = e.target?.error;
                        reject(new CacherError(`Transaction error: ${error?.message || JSON.stringify(error)}`));
                    };
                    const store = tx.objectStore(this.storeName);
                    const result = operation(store, db);
                    if (result) {
                        result.onerror = (e) => {
                            cleanup();
                            const error = e.target?.error;
                            reject(new CacherError(`Cache operation failed: ${error?.message || JSON.stringify(error) || JSON.stringify(e)}`));
                        };
                        result.onsuccess = () => {
                            resolve(result.result);
                        };
                    }
                    else {
                        // Operation doesn't return a request (e.g., cursor operations)
                        resolve(undefined);
                    }
                }
                catch (err) {
                    cleanup();
                    reject(err instanceof Error ? err : new CacherError(String(err)));
                }
            };
        });
    }
    async deleteDBase() {
        return new Promise((res, rej) => {
            const req = window.indexedDB.deleteDatabase(this.dbName);
            req.onerror = (e) => {
                const error = e.target?.error;
                rej(new CacherError(`Failed to delete cache database: ${error?.message || JSON.stringify(error) || JSON.stringify(e)}`));
            };
            req.onblocked = () => {
                console.warn('IndexedDB deleteDatabase is blocked by open connections');
            };
            req.onsuccess = () => {
                res();
            };
        });
    }
    async getEstimate() {
        return new Promise((resolve, reject) => {
            window.navigator.storage.estimate().then((estimate) => {
                if (typeof estimate.quota != 'number' || typeof estimate.usage != 'number' || isNaN(estimate.quota) || isNaN(estimate.usage)) {
                    throw new Error(`Storage quota or usage is not a number. Quota is ${estimate.quota}. Usage is ${estimate.usage}`);
                }
                resolve(estimate);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    async purgeIfQuotaLimit() {
        if (this.isPurging)
            return false;
        return this.withRetry(() => new Promise((resolve, reject) => {
            this.getEstimate().then((estimate) => {
                if (estimate.quota && estimate.usage) {
                    const remainingMb = (estimate.quota - estimate.usage) / (1024 * 1024);
                    const usageMb = estimate.usage / (1024 * 1024);
                    const quotaMb = estimate.quota / (1024 * 1024);
                    // Purge if remaining space is below the threshold
                    if (remainingMb <= this.getQuotaLimitInMb()) {
                        console.warn(`Cache storage critically low. Remaining: ${remainingMb.toFixed(2)}MB, Usage: ${usageMb.toFixed(2)}MB, Total: ${quotaMb.toFixed(2)}MB, Threshold: ${this.getQuotaLimitInMb()}MB`);
                        this.setPurging(true);
                        this.deleteDBase().then(() => {
                            resolve(true);
                        }).catch((error) => {
                            reject(error);
                        }).finally(() => {
                            this.setPurging(false);
                        });
                    }
                    else {
                        resolve(false);
                    }
                }
                else {
                    resolve(false);
                }
            }).catch((error) => {
                reject(error);
            });
        }));
    }
    getQuotaLimitInMb() {
        return this.quotaLimit;
    }
}

export { IndexedDbCacher, LocalStorageCacher };
